<template>
  <div class="alertes-container">
    <div class="alertes-header">
      <img src="@/assets/icons/cle.png" alt="alertes" class="alertes-icon" />
      <span class="alertes-title">Alertes / échéances</span>
      <button @click="loadAlertes" class="alertes-refresh-btn" title="Actualiser les données">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7l3 2.7M3 22v-6h6M21 12a9 9 0 0 1-15 6.7l-3-2.7"></path>
        </svg>
      </button>
    </div>
    <div class="alertes-content">
      <div class="alertes-filtres">
        <input type="text" v-model="searchQuery" placeholder="Chercher par nom" class="alertes-filtre-input" />
        <input type="text" v-model="bienFilter" placeholder="Par Bien" class="alertes-filtre-input" />
        <div class="alertes-filtre-dates">
          <input 
            type="date" 
            class="alertes-filtre-date" 
            v-model="dateFilter.start"
            placeholder="Date de début"
          />
          <span>-</span>
          <input 
            type="date" 
            class="alertes-filtre-date" 
            v-model="dateFilter.end"
            placeholder="Date de fin"
          />
          <button 
            v-if="dateFilter.start || dateFilter.end" 
            @click="clearDateFilter"
            class="clear-date-btn"
            title="Effacer le filtre de date"
          >
            ✕
          </button>
        </div>
      </div>
      <div class="alertes-status-filters">
        <button :class="['alertes-status-btn', 'alertes-status-btn-waiting', { active: filtreStatut === 'attente' }]"
          @click="filtreStatut = 'attente'">
          En attente de règlement <span class="alertes-badge">
            {{ pendingCount }}
          </span>
        </button>
        <button
          :class="['alertes-status-btn', 'alertes-status-btn-paid', { active: filtreStatut === 'reglee' }]"
          @click="filtreStatut = 'reglee'"
        >
          Factures réglées <span class="alertes-badge">{{ paidCount }}</span>
        </button>
      </div>
      
      <!-- Message d'erreur -->
      <div v-if="error" class="alertes-error-message">
        {{ error }}
        <button @click="loadAlertes" class="alertes-retry-btn">Réessayer</button>
      </div>

      <!-- État de chargement -->
      <div v-if="isLoading" class="alertes-loading">
        <div class="alertes-loading-spinner"></div>
        <div class="alertes-loading-text">Chargement des alertes...</div>
      </div>
      
      <table v-if="!isLoading && !error" class="alertes-table">
        <thead>
          <tr>
            <th>Date Création</th>
            <th>Mois</th>
            <th>Client</th>
            <th>PROJET</th>
            <th>Type</th>
            <th>Durée</th>
            <th>Prix</th>
            <th>Échéances</th>
            <th>Date Limite</th>
            <th v-if="filtreStatut !== 'reglee'">Délai</th>
            <th v-if="filtreStatut === 'reglee'">Preuve</th>
            <th v-if="filtreStatut === 'reglee'">Facture</th>
            <th>Status</th>
            <th v-if="filtreStatut !== 'reglee'">Facture</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="alerte in alertesFiltrees" :key="alerte.id" :class="{'alertes-row-urgent': alerte.days_remaining <= 5 && alerte.days_remaining >= 0, 'alertes-row-late': alerte.days_remaining < 0}">
            <td><b>{{ alerte.date }}</b></td>
            <td><span class="month-badge">{{ alerte.mois }}</span></td>
            <td>{{ alerte.client }}</td>
            <td>{{ alerte.projet }}</td>
            <td><b>{{ alerte.type }}</b></td>
            <td>{{ alerte.duree }}</td>
            <td><b>{{ alerte.prix }}</b></td>
            <td>{{ alerte.echeance }}</td>
            <td><b class="due-date">{{ formatDueDate(alerte.due_date) }}</b></td>
            <!-- Délai column - only for non-paid invoices -->
            <td v-if="filtreStatut !== 'reglee'">
              <span :class="{
                'countdown-badge': true,
                'countdown-urgent': alerte.days_remaining <= 5 && alerte.days_remaining >= 0,
                'countdown-late': alerte.days_remaining < 0,
                'countdown-normal': alerte.days_remaining > 5
              }">{{ alerte.days_remaining_text }}</span>
            </td>
            
            <!-- Preuve column - only for paid invoices -->
            <td v-if="filtreStatut === 'reglee'">
              <button 
                v-if="alerte.payment_proof_path" 
                class="file-btn preuve-btn" 
                @click="showFilePopup(alerte.payment_proof_path, 'Preuve de paiement')"
                title="Voir la preuve de paiement">
                📄
              </button>
              <span v-else class="no-file">-</span>
            </td>
            
            <!-- Facture column - only for paid invoices -->
            <td v-if="filtreStatut === 'reglee'">
              <button 
                v-if="alerte.invoice_file_path" 
                class="file-btn facture-btn" 
                @click="showFilePopup(alerte.invoice_file_path, 'Facture')"
                title="Voir la facture">
                🧾
              </button>
              <span v-else class="no-file">-</span>
            </td>
            
            <td>
              <span v-if="alerte.status === 'NR'" class="alertes-status-nr">NR</span>
              <span v-else class="alertes-status-ok">R</span>
            </td>
            
            <!-- Facture column - only for non-paid invoices -->
            <td v-if="filtreStatut !== 'reglee'">
              <button class="alertes-tool-btn" @click="voirFactures(alerte)">🧾</button>
            </td>
            <td>
              <button class="alertes-eye-btn" @click="voirDetail(alerte)">
                <i class="fa fa-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-if="showFactureModal" class="modal-overlay" @click.self="closeFactureModal">
    <div class="facture-modal">
      <div class="facture-modal-header">
        <span>Facture n°: <b>{{ selectedFacture?.numero }}</b></span>
        <span>Date : <b>{{ selectedFacture?.date }}</b></span>
        <span class="facture-modal-close" @click="closeFactureModal">&times;</span>
      </div>
      <div id="facture-content" class="facture-content">
      <div class="facture-modal-infos">
          <div class="company-info">
            <img src="@/assets/logow.png" alt="Logo" class="facture-logo" />
        <div>
              <h2>W PATREMOINE</h2>
              <p>227, Bd d'Anfa, Casablanca<br>
              info@residencewalili.com<br>
              +212 5 22 39 71 39<br>
              +212 5 22 39 74 74</p>
            </div>
        </div>
          <div class="client-info">
            <b>{{ selectedFacture?.client }}</b><br />
            {{ selectedFacture?.projet }}<br />
          mail@email.com<br />
          +212 6 69 977 677
        </div>
      </div>
      <div class="facture-modal-table">
        <table>
          <thead>
            <tr>
              <th>Designation</th>
              <th>Prix unitaire (MAD)</th>
              <th>Quantité</th>
              <th>Total HT (MAD)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
                <td>{{ selectedFacture?.designation }}</td>
                <td>{{ selectedFacture?.prixUnitaire }}</td>
                <td>{{ selectedFacture?.quantite }}</td>
                <td>{{ selectedFacture?.totalHT }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="facture-modal-totaux">
          <div><span>Total HT (MAD)</span><span>{{ selectedFacture?.totalHT }}</span></div>
          <div><span>Taux TVA</span><span>{{ selectedFacture?.tva }}</span></div>
          <div><span>Montant TVA</span><span>{{ selectedFacture?.montantTVA }}</span></div>
          <div class="total-ttc">
            <span>Total TTC (MAD)</span>
            <span>{{ selectedFacture?.totalTTC }}</span>
          </div>
        </div>
      </div>
      <div class="facture-modal-actions">
        <button 
          class="action-btn download-btn" 
          @click="downloadFacture"
          :disabled="isLoading"
        >
          <img src="@/assets/icons/telecharger.png" alt="Télécharger" class="icon-gold-img" />
          {{ isLoading ? 'Téléchargement...' : 'Télécharger' }}
        </button>
        <button 
          class="action-btn print-btn" 
          @click="printFacture"
          :disabled="isLoading"
        >
          <img src="@/assets/icons/imprimer.png" alt="Imprimer" class="icon-gold-img" />
          {{ isLoading ? 'Impression...' : 'Imprimer' }}
        </button>
        <button 
          class="action-btn status-btn" 
          @click="openStatusModal"
          :disabled="isLoading || selectedFacture?.statut === 'R'"
          :class="{'status-btn-paid': selectedFacture?.statut === 'R'}"
        >
          <img 
            :src="selectedFacture?.statut === 'R' ? '@/assets/icons/check.png' : '@/assets/icons/pending.png'" 
            :alt="selectedFacture?.statut === 'R' ? 'Payée' : 'En attente'" 
            class="icon-gold-img" 
          />
          {{ selectedFacture?.statut === 'R' ? 'Payée' : 'Changer Statut' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal de changement de statut -->
  <div v-if="showStatusModal" class="modal-overlay" @click.self="closeStatusModal">
    <div class="status-modal">
      <div class="status-modal-header">
        <h3>Changement de statut</h3>
        <button class="close-btn" @click="closeStatusModal">&times;</button>
      </div>
      <div class="status-modal-content">
        <div class="form-group">
          <label>Date de règlement</label>
          <input 
            type="date" 
            v-model="statusForm.dateReglement"
            class="status-input"
          />
        </div>
        <div class="form-group">
          <label>Type de paiement <span class="required-field">*</span></label>
          <input 
            type="text" 
            v-model="statusForm.typePaiement"
            class="status-input"
            placeholder="Ex: Chèque, Virement, Espèces..."
            required
          />
        </div>
        <div class="form-group">
          <label>Motif de retard</label>
          <textarea 
            v-model="statusForm.motifRetard"
            class="status-input"
            placeholder="Raison du retard de paiement..."
          ></textarea>
        </div>
        <div class="form-group">
          <label class="preuve-upload">
            <span class="preuve-text">FACTURE <span class="required-field">*</span></span>
            <input 
              type="file" 
              @change="handleFactureUpload"
              accept=".pdf,.doc,.docx"
              style="display: none;"
              required
            />
            <div class="upload-zone" :class="{ 'has-file': statusForm.facture }">
              <svg class="upload-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>{{ statusForm.facture ? statusForm.facture.name : 'Télécharger la facture' }}</span>
            </div>
            <div class="preuve-help-text">Téléchargez le fichier de la facture (PDF, DOC, DOCX)</div>
          </label>
        </div>
        <div class="form-group">
          <div class="preuve-preview" v-if="statusForm.facture && statusForm.facturePreview">
            <div class="preuve-pdf-preview">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>{{ statusForm.facture.name }}</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="preuve-upload">
            <span class="preuve-text">PREUVE DE PAIEMENT <span class="required-field">*</span></span>
            <input 
              type="file" 
              @change="handlePreuveUpload"
              accept=".pdf,.jpg,.jpeg,.png"
              style="display: none;"
              required
            />
            <div class="upload-zone" :class="{ 'has-file': statusForm.preuve }">
              <svg class="upload-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>{{ statusForm.preuve ? statusForm.preuve.name : 'Ajouter un document' }}</span>
            </div>
            <div class="preuve-help-text">Ajoutez une preuve de paiement (chèque, reçu, etc.)</div>
          </label>
        </div>
        <div class="form-group">
          <div class="preuve-preview" v-if="statusForm.preuve && statusForm.preuvePreview">
            <img v-if="isImageFile(statusForm.preuve)" :src="statusForm.preuvePreview" alt="Aperçu de la preuve" class="preuve-image" />
            <div v-else class="preuve-pdf-preview">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>{{ statusForm.preuve.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="status-modal-footer">
        <button 
          class="validate-btn" 
          @click="submitStatusChange"
          :disabled="isLoading || !statusForm.preuve || !statusForm.facture"
        >
          {{ isLoading ? 'Traitement en cours...' : 'Valider' }}
        </button>
      </div>
    </div>
  </div>
  
  <!-- File Popup Modal -->
  <div v-if="showFileModal" class="modal-overlay" @click.self="closeFileModal">
    <div class="file-modal">
      <div class="file-modal-header">
        <span>{{ fileModalTitle }}</span>
        <span class="file-modal-close" @click="closeFileModal">&times;</span>
      </div>
      <div class="file-modal-content">
        <div v-if="isImageFilePath(currentFilePath)" class="image-container">
          <img :src="getFileUrl(currentFilePath)" :alt="fileModalTitle" class="file-image" />
        </div>
        <div v-else-if="isPdfFilePath(currentFilePath)" class="pdf-container">
          <iframe :src="getFileUrl(currentFilePath)" class="pdf-viewer" frameborder="0"></iframe>
        </div>
        <div v-else class="unsupported-file">
          <p>Type de fichier non supporté pour l'aperçu</p>
          <a :href="getFileUrl(currentFilePath)" target="_blank" class="download-btn">
            Télécharger le fichier
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import html2pdf from 'html2pdf.js'
import factureService from '@/services/factureService'
import alertService from '@/services/alertService'

// Les alertes seront chargées depuis l'API
const alertes = ref([])

// Badge counts - always show total counts regardless of current filter
const pendingCount = ref(0)
const paidCount = ref(0)

// État de chargement
const isLoading = ref(false)
const error = ref(null)

// Load badge counts separately to always show correct totals
async function loadBadgeCounts() {
  try {
    // Load both pending and paid counts in parallel
    const [pendingResponse, paidResponse] = await Promise.all([
      alertService.getAlertesEnAttente(),
      alertService.getAlertesReglees()
    ]);
    
    // Update badge counts
    if (pendingResponse && pendingResponse.success && pendingResponse.data) {
      pendingCount.value = Array.isArray(pendingResponse.data) ? pendingResponse.data.length : 0;
    }
    
    if (paidResponse && paidResponse.success && paidResponse.data) {
      paidCount.value = Array.isArray(paidResponse.data) ? paidResponse.data.length : 0;
    }
    
    console.log('Badge counts loaded:', { pending: pendingCount.value, paid: paidCount.value });
  } catch (error) {
    console.error('Error loading badge counts:', error);
  }
}

// Chargement des alertes avec le nouveau système
async function loadAlertes() {
  try {
    isLoading.value = true
    error.value = null
    
    console.log('Chargement des données pour:', filtreStatut.value);
    
    // Always load badge counts to keep them accurate
    await loadBadgeCounts();
    
    let alertsResponse;
    
    // Load different data based on the current filter
    if (filtreStatut.value === 'reglee') {
      // Load paid invoices from invoices table
      console.log('Chargement des factures réglées depuis la table invoices...');
      alertsResponse = await alertService.getAlertesReglees();
    } else {
      // Load pending alerts from alerts table (default case)
      console.log('Chargement des alertes en attente depuis la table alerts...');
      alertsResponse = await alertService.getAlertesEnAttente();
    }
    
    console.log('Réponse des données:', alertsResponse);
    
    if (alertsResponse && alertsResponse.success && alertsResponse.data) {
      const alertsData = Array.isArray(alertsResponse.data) ? alertsResponse.data : [];
      console.log('Données trouvées:', alertsData.length);
      
      // Les données sont déjà transformées par le backend
      alertes.value = alertsData.map(alert => ({
        id: alert.id,
        date: alert.date,
        mois: alert.mois,
        client: alert.client,
        projet: alert.projet,
        type: alert.type,
        duree: alert.duree,
        prix: alert.prix,
        echeance: alert.echeance,
        due_date: alert.due_date,
        days_remaining: alert.days_remaining,
        days_remaining_text: alert.days_remaining_text,
        status: alert.status,
        contract_id: alert.contract_id,
        invoice_id: alert.invoice_id,
        invoice: alert.invoice,
        contract: alert.contract,
        payment_date: alert.payment_date,
        payment_method: alert.payment_method,
        notes: alert.notes,
        payment_proof_path: alert.payment_proof_path,
        invoice_file_path: alert.invoice_file_path,
        // Facture pour la modal (données enrichies)
        facture: {
          designation: `LOCATION ${alert.projet}`,
          prixUnitaire: parseFloat(alert.prix.replace(/[^\d.-]/g, '')),
          quantite: '1',
          totalHT: parseFloat(alert.prix.replace(/[^\d.-]/g, '')),
          tva: '14%',
          montantTVA: (parseFloat(alert.prix.replace(/[^\d.-]/g, '')) * 0.14).toFixed(2),
          totalTTC: (parseFloat(alert.prix.replace(/[^\d.-]/g, '')) * 1.14).toFixed(2)
        }
      }));
      
      console.log('Données chargées avec succès:', alertes.value.length);
    } else {
      console.log('Aucune donnée trouvée');
      alertes.value = [];
    }
    
  } catch (err) {
    console.error('Erreur lors du chargement des alertes:', err);
    
    // Fallback to old system if new system fails
    try {
      console.log('Retour vers l\'ancien système de factures...');
      const unpaidResponse = await factureService.getFacturesEnAttente();
      
      if (unpaidResponse && unpaidResponse.data) {
        let unpaidInvoices = Array.isArray(unpaidResponse.data) ? unpaidResponse.data : unpaidResponse.data.data || [];
        
        if (unpaidInvoices.length > 0) {
          // Transformer les factures en format d'alerte
          alertes.value = unpaidInvoices.map(invoice => ({
            id: invoice.id,
            date: formatDate(invoice.created_at || new Date()),
            mois: invoice.period || 'N/A',
            client: invoice.contract?.tenant?.name || 'N/A',
            projet: invoice.contract?.property?.name || 'N/A',
            type: 'LOCATION',
            duree: invoice.contract?.duration ? `${invoice.contract.duration} mois` : 'N/A',
            prix: `${invoice.total_amount} MAD`,
            echeance: getPaymentFrequencyText(invoice.contract?.payment_frequency),
            due_date: invoice.due_date,
            days_remaining: calculateDaysRemaining(invoice.due_date),
            days_remaining_text: getDaysRemainingText(calculateDaysRemaining(invoice.due_date)),
            status: invoice.status === 'paid' ? 'R' : 'NR',
            contract_id: invoice.contract_id,
            invoice_id: invoice.id,
            invoice: invoice,
            contract: invoice.contract,
            facture: {
              designation: `LOCATION ${invoice.contract?.property?.name || ''}`,
              prixUnitaire: invoice.total_amount,
              quantite: '1',
              totalHT: invoice.amount_excl_tax || invoice.total_amount,
              tva: '14%',
              montantTVA: invoice.tax_amount || (invoice.total_amount * 0.14).toFixed(2),
              totalTTC: invoice.total_amount
            }
          }));
          
          console.log('Alertes de secours chargées:', alertes.value.length);
        }
      }
    } catch (fallbackErr) {
      console.error('Le système de secours a également échoué:', fallbackErr);
      error.value = 'Erreur lors du chargement des alertes. Veuillez réessayer.';
      alertes.value = [];
    }
  } finally {
    isLoading.value = false
  }
}

// Format due date for display
function formatDueDate(dateString) {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'Non spécifié';
  }
  try {
    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Date invalide:', dateString);
      return 'Date invalide';
    }
    return date.toLocaleDateString('fr-FR');
  } catch (error) {
    console.error('Erreur lors du formatage de la date d\'échéance:', error, 'Entrée:', dateString);
    return 'Date invalide';
  }
}

// Format date for display (alias for formatDueDate)
function formatDate(dateString) {
  return formatDueDate(dateString);
}

// Calculate days remaining until due date
function calculateDaysRemaining(dueDate) {
  if (!dueDate) return 0;
  try {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    console.error('Erreur lors du calcul des jours restants:', error);
    return 0;
  }
}

// Get payment frequency text in French
function getPaymentFrequencyText(frequency) {
  const frequencies = {
    'monthly': 'Mensuel',
    'quarterly': 'Trimestriel',  
    'semestriel': 'Semestriel',
    'annuel': 'Annuel'
  };
  return frequencies[frequency] || frequency || 'N/A';
}

// Get formatted days remaining text
function getDaysRemainingText(daysRemaining) {
  if (daysRemaining === null || daysRemaining === undefined) {
    return 'N/A';
  }
  
  if (daysRemaining > 0) {
    return daysRemaining + ' jour(s)';
  } else if (daysRemaining === 0) {
    return 'Aujourd\'hui';
  } else {
    return Math.abs(daysRemaining) + ' jour(s) de retard';
  }
}

// Chargement initial des alertes
onMounted(() => {
  // Check for filter parameter in URL
  const route = useRoute()
  const filterParam = route.query.filter
  
  if (filterParam && ['attente', 'reglee'].includes(filterParam)) {
    filtreStatut.value = filterParam
  }
  
  loadAlertes()
})

const filtreStatut = ref('attente') // 'attente', 'reglee' - default to 'attente' to show unpaid invoices
const showFactureModal = ref(false)
const selectedFacture = ref(null)
const searchQuery = ref('')
const bienFilter = ref('')

// File popup modal variables
const showFileModal = ref(false)
const currentFilePath = ref('')
const fileModalTitle = ref('')

// Watch for filter changes and reload data
watch(filtreStatut, (newFilter) => {
  console.log('Filter changed to:', newFilter);
  loadAlertes();
})

const alertesFiltrees = computed(() => {
  // Since we now load data based on filter, no need to filter by status here
  // The data is already the correct type based on the API call
  let filteredAlertes = alertes.value;
  
  // Filtrer par recherche si nécessaire
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filteredAlertes = filteredAlertes.filter(alerte => 
      alerte.client.toLowerCase().includes(query) || 
      alerte.projet.toLowerCase().includes(query)
    );
  }
  
  // Filtrer par bien si nécessaire
  if (bienFilter.value) {
    const bienQuery = bienFilter.value.toLowerCase();
    filteredAlertes = filteredAlertes.filter(alerte => 
      alerte.projet.toLowerCase().includes(bienQuery)
    );
  }
  
  // Filter by date range
  if (dateFilter.value.start || dateFilter.value.end) {
    filteredAlertes = filteredAlertes.filter(alerte => {
      // Check due_date for filtering
      return isDateInRange(alerte.due_date)
    });
  }
  
  return filteredAlertes;
})

function voirDetail(alerte) {
  // À implémenter : ouvrir un modal ou afficher le détail de l'alerte
  alert('Détail de l\'alerte pour ' + alerte.client)
}

async function voirFactures(alerte) {
  try {
    isLoading.value = true
    
    // Essayer de récupérer la facture depuis l'API si nous avons un ID
    if (alerte.id) {
      try {
        console.log('Récupération de la facture pour l\'ID d\'alerte:', alerte.id)
        const response = await factureService.getFactureById(alerte.id)
        
        if (response && response.data) {
          console.log('Données de facture depuis l\'API:', response.data)
          // Utiliser les données de l'API
          selectedFacture.value = {
            id: response.data.id,
            numero: response.data.reference || "LC 00029/2025",
            date: alerte.date,
            client: alerte.client,
            projet: alerte.projet,
            type: alerte.type,
            prix: alerte.prix,
            duree: alerte.duree,
            statut: alerte.status,
            ...alerte.facture
          }
        } else {
          throw new Error('Aucune donnée retournée par l\'API')
        }
      } catch (apiError) {
        console.warn('Impossible de récupérer la facture depuis l\'API:', apiError)
        // Fallback to local data
        selectedFacture.value = {
          id: alerte.id, // Important: Use the alerte ID as invoice ID
          numero: "LC 00029/2025",
          date: alerte.date,
          client: alerte.client,
          projet: alerte.projet,
          type: alerte.type,
          prix: alerte.prix,
          duree: alerte.duree,
          statut: alerte.status,
          ...alerte.facture
        }
      }
    } else {
      // Si pas d'ID, utiliser les données locales
      console.log('Aucun ID disponible, utilisation des données locales')
      selectedFacture.value = {
        numero: "LC 00029/2025",
        date: alerte.date,
        client: alerte.client,
        projet: alerte.projet,
        type: alerte.type,
        prix: alerte.prix,
        duree: alerte.duree,
        statut: alerte.status,
        ...alerte.facture
      }
    }
    
    console.log('Facture sélectionnée pour changement de statut:', selectedFacture.value)
    // Go directly to status change modal instead of showing invoice
    openStatusModal()
  } catch (error) {
    console.error('Erreur lors du chargement de la facture:', error)
    alert('Erreur lors du chargement de la facture')
  } finally {
    isLoading.value = false
  }
}

function closeFactureModal() {
  showFactureModal.value = false
  selectedFacture.value = null
}

async function generatePDF(saveToFile = true) {
  const content = document.getElementById('facture-content')
  if (!content) {
    console.error('Cannot find facture-content element')
    throw new Error('PDF generation failed: content element not found')
  }
  
  const filename = `facture_${selectedFacture.value.numero.replace(/[/\s:]/g, '_')}.pdf`
  
  console.log('Starting PDF generation for', filename)
  console.log('Content element height:', content.clientHeight)
  
  const opt = {
    margin: 10,
    filename: filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      height: content.clientHeight,
      windowHeight: content.clientHeight
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait'
    }
  }
  
  if (saveToFile) {
    // Save to file (download)
    try {
      console.log('Generating PDF for download')
      return await html2pdf().set(opt).from(content).save()
    } catch (err) {
      console.error('Error generating downloadable PDF:', err)
      throw err
    }
  } else {
    // Return as blob/file object with improved error handling
    return new Promise((resolve, reject) => {
      console.log('Generating PDF as blob')
      const worker = html2pdf().set(opt).from(content)
      
      worker.outputPdf('blob')
        .then(blob => {
          console.log('Blob generated successfully, size:', blob.size)
          
          try {
            // Ensure we have a valid blob before creating a File
            if (!(blob instanceof Blob) || blob.size === 0) {
              throw new Error('Invalid blob generated')
            }
            
            // Simple filename without special characters
            const safeFilename = `invoice_${Date.now()}.pdf`
            
            // Convert blob to File object
            const file = new File([blob], safeFilename, { 
              type: 'application/pdf',
              lastModified: new Date().getTime()
            })
            
            console.log('File object created successfully:', file.name, file.size, file.type)
            resolve(file)
          } catch (fileError) {
            console.error('Error creating File from blob:', fileError)
            reject(fileError)
          }
        })
        .catch(error => {
          console.error('Error in PDF blob generation:', error)
          reject(error)
        })
    })
  }
}

async function downloadFacture() {
  try {
    isLoading.value = true
    await generatePDF()
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error)
    alert('Erreur lors du téléchargement de la facture')
  } finally {
    isLoading.value = false
  }
}

async function printFacture() {
  try {
    isLoading.value = true
    const content = document.getElementById('facture-content')
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
        <head>
          <title>Impression Facture ${selectedFacture.value.numero}</title>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              min-height: 100vh;
            }
            .facture-content {
              padding: 20px;
              min-height: 100vh;
              box-sizing: border-box;
            }
            .company-info {
              display: flex;
              align-items: flex-start;
              gap: 20px;
              margin-bottom: 40px;
            }
            .facture-logo {
              width: 150px;
              height: auto;
            }
            .client-info {
              margin-top: 20px;
              margin-bottom: 40px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 30px 0;
            }
            th, td {
              padding: 12px;
              border: 1px solid #ddd;
              text-align: left;
            }
            th {
              background: #22305a;
              color: white;
            }
            .totaux {
              margin-top: 40px;
              border-top: 2px solid #22305a;
              padding-top: 20px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin: 10px 0;
              font-size: 14px;
            }
            .total-ttc {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 2px solid #22305a;
              font-weight: bold;
              font-size: 16px;
            }
            @media print {
              .facture-content {
                page-break-after: always;
              }
            }
          </style>
        </head>
        <body>
          <div class="facture-content">
            <div class="company-info">
              <img src="${window.location.origin}/src/assets/logow.png" alt="Logo" class="facture-logo" />
              <div>
                <h2>W PATREMOINE</h2>
                <p>227, Bd d'Anfa, Casablanca<br>
                info@residencewalili.com<br>
                +212 5 22 39 71 39<br>
                +212 5 22 39 74 74</p>
              </div>
            </div>
            
            <div style="text-align: right; margin-bottom: 30px;">
              <h3>Facture N° ${selectedFacture.value.numero}</h3>
              <p>Date : ${selectedFacture.value.date}</p>
            </div>

            <div class="client-info">
              <h3>FACTURÉ À</h3>
              <p><strong>${selectedFacture.value.client}</strong><br>
              ${selectedFacture.value.projet}<br>
              mail@email.com<br>
              +212 6 69 977 677</p>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Designation</th>
                  <th>Prix unitaire (MAD)</th>
                  <th>Quantité</th>
                  <th>Total HT (MAD)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${selectedFacture.value.designation}</td>
                  <td>${selectedFacture.value.prixUnitaire}</td>
                  <td>${selectedFacture.value.quantite}</td>
                  <td>${selectedFacture.value.totalHT}</td>
                </tr>
              </tbody>
            </table>

            <div class="totaux">
              <div class="total-row">
                <span>Total HT (MAD)</span>
                <span>${selectedFacture.value.totalHT}</span>
              </div>
              <div class="total-row">
                <span>Taux TVA</span>
                <span>${selectedFacture.value.tva}</span>
              </div>
              <div class="total-row">
                <span>Montant TVA</span>
                <span>${selectedFacture.value.montantTVA}</span>
              </div>
              <div class="total-ttc">
                <span>Total TTC (MAD)</span>
                <span>${selectedFacture.value.totalTTC}</span>
              </div>
            </div>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  } catch (error) {
    console.error('Erreur lors de l\'impression:', error)
    alert('Erreur lors de l\'impression de la facture')
  } finally {
    isLoading.value = false
  }
}

async function submitStatusChange() {
  try {
    isLoading.value = true
    
    console.log('Mise à jour du statut de paiement vers payé:', selectedFacture.value)
    
    // Find the invoice ID and contract ID from the selected alerte
    let invoiceId = null;
    let contractId = null;
    
    const currentAlerte = alertes.value.find(a => a.id === selectedFacture.value?.id);
    
    if (currentAlerte) {
      invoiceId = currentAlerte.invoice_id || (currentAlerte.invoice && currentAlerte.invoice.id);
      contractId = currentAlerte.contract_id;
    }
    
    if (!invoiceId) {
      console.log('Aucun ID de facture trouvé - création d\'une nouvelle facture pour cette alerte');
      try {
        // Create invoice from the alert data
        if (!contractId) {
          console.error('Aucun ID de contrat trouvé - impossible de créer une facture');
          alert('Erreur: Impossible de trouver l\'ID du contrat pour créer une facture.');
          return;
        }
        
        console.log('Création d\'une nouvelle facture pour le contrat:', contractId);
        
        // Create invoice with FormData to support file uploads
        const createFormData = new FormData()
        createFormData.append('contract_id', contractId)
        createFormData.append('issue_date', new Date().toISOString().split('T')[0])
        createFormData.append('due_date', currentAlerte.due_date)
        createFormData.append('period', currentAlerte.mois)
        createFormData.append('amount_excl_tax', parseFloat(currentAlerte.prix.replace(/[^\d.-]/g, '')))
        createFormData.append('tax_amount', parseFloat(currentAlerte.prix.replace(/[^\d.-]/g, '')) * 0.14)
        createFormData.append('total_amount', parseFloat(currentAlerte.prix.replace(/[^\d.-]/g, '')) * 1.14)

        // Add validation before creating the invoice (around line 975)
        if (!statusForm.value.typePaiement || statusForm.value.typePaiement.trim() === '') {
          alert('Veuillez spécifier le type de paiement');
          return;
        }

        // Then use it without fallback since it's now required
        createFormData.append('payment_method', statusForm.value.typePaiement)

        // Use alert description for notes
        let notes = currentAlerte.description || 'Facture créée depuis une alerte'
        if (statusForm.value.motifRetard && statusForm.value.motifRetard.trim()) {
          notes += `\nMotif de retard: ${statusForm.value.motifRetard}`
        }
        createFormData.append('notes', notes)

        // Add payment proof
        if (statusForm.value.preuve) {
          createFormData.append('payment_proof', statusForm.value.preuve)
        }

        // Add invoice file
        if (statusForm.value.facture) {
          createFormData.append('invoice_file', statusForm.value.facture)
        } else {
          throw new Error('Fichier de facture requis pour la création')
        }
        
        console.log('Creating invoice with FormData...')
        const response = await factureService.createFactureWithFiles(createFormData);
        
        console.log('New invoice created:', response);
        
        if (response && response.data && response.data.id) {
          // Use the newly created invoice ID
          invoiceId = response.data.id;
          
          // Update the alert with the new invoice ID
          await alertService.updateAlerte(currentAlerte.id, { invoice_id: invoiceId });
          console.log('Alert updated with new invoice ID:', invoiceId);
        } else {
          throw new Error('Failed to get new invoice ID from response');
        }
      } catch (createError) {
        console.error('Failed to create invoice:', createError);
        if (createError.response && createError.response.data) {
          console.error('Server response:', createError.response.data);
        }
        alert('Erreur lors de la création de la facture. Veuillez réessayer.');
        return;
      }
    }
    
    // Prepare the status update data
    const formData = new FormData()
    formData.append('status', 'paid')
    // Always use current date when validating (clicking "Valider")
    formData.append('payment_date', new Date().toISOString().split('T')[0])
    // Always include payment method, use provided value or default
    formData.append('payment_method', statusForm.value.typePaiement || 'Paiement manuel')
    
    // Add motif de retard to notes if provided
    if (statusForm.value.motifRetard && statusForm.value.motifRetard.trim()) {
      formData.append('notes', `Motif de retard: ${statusForm.value.motifRetard}`)
    }
    
    // Add payment proof if provided
    if (statusForm.value.preuve) {
      formData.append('payment_proof', statusForm.value.preuve)
    }
    
    // Add invoice file if provided
    if (statusForm.value.facture) {
      formData.append('invoice_file', statusForm.value.facture)
    }
    
    // Add PDF to status update if invoice doesn't already have one
    try {
      console.log('Checking if we need to add PDF to status update for invoice:', invoiceId)
      
      // DISABLED: Automatic PDF generation removed - users must upload files manually
      // If we just created the invoice above, it should already have the PDF
      // DISABLED: Automatic PDF generation removed
      // Users must upload invoice files manually via the form
      console.log('Skipping automatic PDF generation - using user-uploaded file instead')
      
      // Debug information
      console.log('FormData contents:')
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]))
      }
      
      console.log('Updating invoice status via API, invoice ID:', invoiceId)
      
      // Call the invoice status update API (this will automatically generate next alert)
      const response = await factureService.updateFactureStatus(invoiceId, formData)
      console.log('Invoice status updated successfully:', response)
      
      // Get the updated invoice data from the response
      const updatedInvoice = response.data || response
      
      // Update the current alert status to paid
      if (currentAlerte) {
        // Use alert service to update the alert status
        try {
          await alertService.updateAlerte(currentAlerte.id, { status: 'paid' })
          console.log('Alert status updated to paid')
          
          // Explicitly generate next payment alert
          try {
            console.log('Generating next payment alert for contract:', contractId, 'after paid invoice:', invoiceId)
            const nextAlertResponse = await alertService.generateNextPaymentAlert(contractId, invoiceId)
            console.log('Next payment alert generated:', nextAlertResponse)
            
            // Update success message based on response
            if (nextAlertResponse.success && nextAlertResponse.data) {
              console.log('Échéance marquée comme réglée et nouvelle échéance générée:', nextAlertResponse.data)
            }
          } catch (nextAlertError) {
            console.warn('Failed to generate next payment alert:', nextAlertError)
            // Continue anyway since the alert was marked as paid
          }
        } catch (alertUpdateError) {
          console.warn('Failed to update alert status:', alertUpdateError)
          // Continue anyway since the main invoice update succeeded
        }
      }
      
      // Update the UI with the correct file paths from backend response
      const alerteIndex = alertes.value.findIndex(a => a.id === selectedFacture.value.id)
      if (alerteIndex !== -1) {
        alertes.value[alerteIndex].status = 'R'
        if (selectedFacture.value) {
          selectedFacture.value.statut = 'R'
        }
        
        // Add payment information using backend response data
        alertes.value[alerteIndex].payment_date = statusForm.value.dateReglement
        alertes.value[alerteIndex].payment_method = statusForm.value.typePaiement
        
        // Use the file paths from backend response instead of just file names
        if (updatedInvoice) {
          alertes.value[alerteIndex].payment_proof_path = updatedInvoice.payment_proof_path || null
          alertes.value[alerteIndex].invoice_file_path = updatedInvoice.invoice_file_path || updatedInvoice.document_path || null
          
          console.log('Updated alert with file paths:', {
            payment_proof_path: updatedInvoice.payment_proof_path,
            invoice_file_path: updatedInvoice.invoice_file_path || updatedInvoice.document_path
          })
        }
      }
      
      // Show success message
      alert('Échéance marquée comme réglée avec succès! La prochaine échéance a été générée automatiquement avec la nouvelle période de paiement.')
      closeStatusModal()
      
      // Reload alerts to show the new generated alert
      setTimeout(() => {
        loadAlertes()
      }, 1000)
      
    } catch (apiError) {
      console.error('Error updating invoice status:', apiError)
      
      // Show detailed error message to user
      let errorMessage = 'Erreur lors de la mise à jour du statut: ';
      if (apiError.response && apiError.response.data) {
        if (apiError.response.data.message) {
          errorMessage += apiError.response.data.message;
        } else if (apiError.response.data.errors) {
          const errors = Object.entries(apiError.response.data.errors)
            .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
            .join('\n');
          errorMessage += '\n' + errors;
        }
      } else {
        errorMessage += 'Veuillez réessayer.';
      }
      
      alert(errorMessage);
    }
  } catch (error) {
    console.error('Erreur lors du changement de statut:', error)
    alert('Erreur lors du changement de statut')
  } finally {
    isLoading.value = false
  }
}

const showStatusModal = ref(false)
const statusForm = ref({
  dateReglement: new Date().toISOString().split('T')[0],
  typePaiement: '',
  motifRetard: '',
  preuve: null,
  preuvePreview: null,
  facture: null,
  facturePreview: null
})

function openStatusModal() {
  showStatusModal.value = true
}

function closeStatusModal() {
  showStatusModal.value = false
  statusForm.value = {
    dateReglement: new Date().toISOString().split('T')[0],
    typePaiement: '',
    motifRetard: '',
    preuve: null,
    preuvePreview: null,
    facture: null,
    facturePreview: null
  }
}

function handleFactureUpload(event) {
  const file = event.target.files[0]
  if (file) {
    statusForm.value.facture = file
    
    // For documents, we just set a flag to show the preview
    if (file.type === 'application/pdf' || file.type.includes('document') || file.type.includes('word')) {
      statusForm.value.facturePreview = 'document'
    }
  }
}

function handlePreuveUpload(event) {
  const file = event.target.files[0]
  if (file) {
    statusForm.value.preuve = file
    
    // Créer un aperçu pour les images
    if (isImageFile(file)) {
      const reader = new FileReader()
      reader.onload = (e) => {
        statusForm.value.preuvePreview = e.target.result
      }
      reader.readAsDataURL(file)
    } else if (file.type === 'application/pdf') {
      // Pour les PDF, on affiche juste une icône
      statusForm.value.preuvePreview = 'pdf'
    }
  }
}

function isImageFile(file) {
  return file && file.type && file.type.startsWith('image/')
}

// File popup functions
function showFilePopup(filePath, title) {
  if (!filePath) {
    alert('Fichier non disponible')
    return
  }
  
  // Open file in new window/tab
  const fileUrl = `http://localhost:8000/storage/${filePath}`
  window.open(fileUrl, '_blank')
}

function closeFileModal() {
  showFileModal.value = false
  currentFilePath.value = ''
  fileModalTitle.value = ''
}

function getFileUrl(filePath) {
  if (!filePath) return ''
  // Remove 'public/' from the path if it exists and add the storage URL
  const cleanPath = filePath.replace('public/', '')
  return `/storage/${cleanPath}`
}

function isImageFilePath(path) {
  if (!path) return false
  const ext = path.toLowerCase().split('.').pop()
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)
}

function isPdfFilePath(path) {
  if (!path) return false
  const ext = path.toLowerCase().split('.').pop()
  return ext === 'pdf'
}

// Add after line 586 (around the other reactive variables):
const dateFilter = ref({
  start: '',
  end: ''
})

// Add this helper function around line 567:
function clearDateFilter() {
  dateFilter.value.start = ''
  dateFilter.value.end = ''
}

// Function to check if a date is within the filter range
function isDateInRange(dateString) {
  if (!dateFilter.value.start && !dateFilter.value.end) {
    return true // No date filter applied
  }
  
  if (!dateString) {
    return false // No date to compare
  }
  
  try {
    const checkDate = new Date(dateString)
    const startDate = dateFilter.value.start ? new Date(dateFilter.value.start) : null
    const endDate = dateFilter.value.end ? new Date(dateFilter.value.end) : null
    
    // If only start date is set
    if (startDate && !endDate) {
      return checkDate >= startDate
    }
    
    // If only end date is set
    if (!startDate && endDate) {
      return checkDate <= endDate
    }
    
    // If both dates are set
    if (startDate && endDate) {
      return checkDate >= startDate && checkDate <= endDate
    }
    
    return true
  } catch (error) {
    console.error('Error comparing dates:', error)
    return true // If there's an error, don't filter out the item
  }
}
</script>

<script>
export default {
  name: 'AlertesView'
}
</script>

<style scoped>
.alertes-container {
  margin-left: 270px;
  min-height: calc(100vh - 70px);
  background: #f5f7fa;
  padding: 0 24px 24px 24px;
  display: flex;
  flex-direction: column;
}

.alertes-header {
  display: flex;
  align-items: center;
  margin: 24px 0 24px 0;
}

.alertes-icon {
  width: 38px;
  height: 38px;
  margin-right: 12px;
}

.alertes-title {
  font-size: 2rem;
  font-weight: 700;
  color: #22305a;
}

.alertes-refresh-btn {
  margin-left: auto;
  background: #22305a;
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.alertes-refresh-btn:hover {
  background: #1a2647;
  transform: rotate(30deg);
}

.alertes-refresh-btn:active {
  transform: rotate(60deg);
}

.alertes-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.alertes-filtres {
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
  justify-content: center;
}

.alertes-filtre-input {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  width: 180px;
  background: #f5f9fc;
}

.alertes-filtre-dates {
  display: flex;
  align-items: center;
  gap: 8px;
}

.alertes-filtre-date {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  background: #f5f9fc;
}

.alertes-status-filters {
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
  justify-content: center;
}

.alertes-status-btn {
  background: #f5f5f5;
  color: #555;
  border: none;
  border-radius: 8px;
  padding: 9px 150px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.alertes-status-btn-waiting {
  background: #c00;
  color: #fff;
  position: relative;
}

.alertes-status-btn-paid {
  background: #1656c1;
  color: #fff;
}

.alertes-badge {
  background: #fff;
  color: #c00;
  border-radius: 50%;
  padding: 2px 8px;
  font-size: 0.95em;
  margin-left: 8px;
  font-weight: 700;
}

.alertes-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

.alertes-table th,
.alertes-table td {
  padding: 12px 10px;
  text-align: left;
  font-size: 1rem;
}

.alertes-table th {
  background: #bfc7d5;
  color: #22305a;
  font-weight: 700;
}

.alertes-table tr:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.alertes-status-nr {
  color: #c00;
  font-weight: 700;
}

.alertes-eye-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: #22305a;
}

.alertes-status-btn.active {
  outline: 2px solid #22305a;
  outline-offset: 2px;
}

.alertes-status-ok {
  color: #3ba935;
  font-weight: 700;
}

.month-badge {
  background-color: #22305a;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 700;
  display: inline-block;
  min-width: 100px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
}

.alertes-tool-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #1656c1;
  padding: 4px 8px;
  border-radius: 50%;
  transition: background 0.2s;
}

.alertes-tool-btn:hover {
  background: #e6e6e6;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.facture-modal {
  background: #fff;
  border-radius: 12px;
  min-width: 650px;
  max-width: 900px;
  min-height: 520px;
  max-height: 700px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 0 32px 0;
}

.facture-modal-header {
  background: #a00;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.facture-modal-close {
  font-size: 2.2rem;
  font-weight: 400;
  cursor: pointer;
  margin-left: 18px;
  position: absolute;
  right: 18px;
  top: 10px;
}

.facture-modal-infos {
  display: flex;
  justify-content: space-between;
  padding: 18px 24px 0 24px;
  font-size: 1.1rem;
  color: #22305a;
}

.facture-modal-table {
  padding: 18px 24px 0 24px;
}

.facture-modal-table table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.facture-modal-table th,
.facture-modal-table td {
  padding: 10px 8px;
  text-align: left;
  font-size: 1rem;
}

.facture-modal-table th {
  background: #22305a;
  color: #fff;
  font-weight: 700;
}

.facture-modal-totaux {
  padding: 18px 24px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.facture-modal-totaux div {
  display: flex;
  justify-content: space-between;
  font-size: 1.05rem;
}

.tva-label {
  font-weight: 700;
  color: #22305a;
}

.tva-value {
  background: #22305a;
  color: #fff;
  padding: 2px 12px;
  border-radius: 6px;
}

/* Styles des boutons d'action */
.facture-modal-actions {
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 24px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  position: sticky;
  bottom: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 160px;
  justify-content: center;
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.download-btn {
  background: #22305a;
  color: white;
}

.download-btn:hover:not(:disabled) {
  background: #1a2647;
  transform: translateY(-1px);
}

.print-btn {
  background: #4a90e2;
  color: white;
}

.print-btn:hover:not(:disabled) {
  background: #357abd;
  transform: translateY(-1px);
}

.status-btn {
  background: #e74c3c;
  color: white;
  margin-left: auto;
}

.status-btn:hover:not(:disabled) {
  background: #c0392b;
  transform: translateY(-1px);
}

.status-btn-paid {
  background: #27ae60;
}

.status-btn-paid:hover:not(:disabled) {
  background: #219a52;
}

.icon-gold-img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* Animation pour les boutons */
.action-btn:active:not(:disabled) {
  transform: translateY(1px);
}

/* Effet de loading */
.action-btn:disabled {
  position: relative;
  overflow: hidden;
}

.action-btn:disabled::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.facture-content {
  padding: 30px;
  min-height: 100vh;
  box-sizing: border-box;
  background: white;
}

.company-info {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 40px;
}

.company-info h2 {
  color: #22305a;
  margin: 0 0 10px 0;
}

.company-info p {
  margin: 0;
  line-height: 1.5;
}

.facture-logo {
  width: 150px;
  height: auto;
}

.facture-header {
  text-align: right;
  margin-bottom: 30px;
}

.facture-header h3 {
  color: #22305a;
  margin: 0 0 5px 0;
}

.facture-header p {
  margin: 0;
}

.client-info {
  margin-bottom: 40px;
}

.client-info h3 {
  color: #22305a;
  margin: 0 0 10px 0;
}

.client-info p {
  margin: 0;
  line-height: 1.5;
}

.facture-table {
  margin: 30px 0;
}

.facture-table table {
  width: 100%;
  border-collapse: collapse;
}

.facture-table th,
.facture-table td {
  padding: 12px;
  border: 1px solid #ddd;
  text-align: left;
}

.facture-table th {
  background: #22305a;
  color: white;
}

.totaux {
  margin-top: 40px;
  border-top: 2px solid #22305a;
  padding-top: 20px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  font-size: 14px;
}

.total-ttc {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #22305a;
  font-weight: bold;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
}

@media print {
  .facture-content {
    page-break-after: always;
  }
}

.status-modal {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 90vw;
  max-height: 85vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.status-modal-header {
  background: #22305a;
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.status-modal-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(85vh - 120px); /* Subtract header and footer height */
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #22305a;
  font-weight: 600;
  font-size: 0.9rem;
}

.status-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.status-input:focus {
  outline: none;
  border-color: #22305a;
}

textarea.status-input {
  min-height: 80px;
  resize: vertical;
}

.preuve-upload {
  cursor: pointer;
  display: block;
}

.preuve-text {
  color: #22305a;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 8px;
  display: block;
}

.upload-zone {
  border: 2px dashed #ddd;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f8f9fa;
}

.upload-zone:hover {
  border-color: #22305a;
  background: #f0f2f5;
}

.upload-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.status-modal-footer {
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.validate-btn {
  background: #27ae60;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.validate-btn:hover:not(:disabled) {
  background: #219a52;
}

.validate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.required-field {
  color: #e74c3c;
  font-weight: bold;
}

.preuve-help-text {
  font-size: 0.8rem;
  color: #666;
  margin-top: 5px;
}

.upload-zone.has-file {
  border-color: #27ae60;
  background-color: #eafaf1;
}

.preuve-preview {
  margin-top: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  background: #f8f9fa;
}

.preuve-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

.preuve-pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 15px;
}

.preuve-pdf-preview svg {
  color: #e74c3c;
}

.preuve-pdf-preview span {
  font-size: 0.9rem;
  color: #333;
  word-break: break-all;
}

/* Styles pour les états de chargement et d'erreur */
.alertes-error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 12px 20px;
  border-radius: 8px;
  margin: 20px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.alertes-retry-btn {
  background: #c62828;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 12px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.alertes-retry-btn:hover {
  background: #b71c1c;
}

.alertes-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
}

.alertes-loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #cbb87c;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.alertes-loading-text {
  font-size: 1.1rem;
  color: #22305a;
  font-weight: 500;
}

/* Styles pour les délais et les lignes urgentes */
.countdown-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
  min-width: 80px;
}

.due-date {
  color: #1656c1;
  font-weight: 600;
}

.countdown-normal {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.countdown-urgent {
  background-color: #fff3e0;
  color: #e65100;
  animation: pulse 1.5s infinite;
}

.countdown-late {
  background-color: #ffebee;
  color: #c62828;
}

.alertes-row-urgent {
  background-color: rgba(255, 243, 224, 0.3);
}

.alertes-row-late {
  background-color: rgba(255, 235, 238, 0.3);
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* File buttons styles */
.file-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.file-btn:hover {
  background: #f0f0f0;
}

.preuve-btn {
  color: #1656c1;
}

.facture-btn {
  color: #22305a;
}

.no-file {
  color: #999;
  font-style: italic;
}

/* File modal styles */
.file-modal {
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.file-modal-header {
  background: #22305a;
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.file-modal-close {
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.file-modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.file-modal-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.file-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.pdf-container {
  width: 100%;
  height: 70vh;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.unsupported-file {
  text-align: center;
  color: #666;
}

.download-btn {
  display: inline-block;
  background: #22305a;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  margin-top: 16px;
  transition: background 0.2s;
}

.download-btn:hover {
  background: #1a2647;
  color: white;
  text-decoration: none;
}

/* Add this CSS after the existing date filter styles (around line 1365): */
.alertes-filtre-dates {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-date-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
}

.clear-date-btn:hover {
  background: #c82333;
}

.alertes-filtre-date {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  background: #f5f9fc;
  min-width: 150px;
}

.alertes-filtre-date:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}
</style>

